import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/shared/api';
import { isMenuKey, MENU_PERMISSIONS_FALLBACK, type MenuKey } from './menuKeys';
import { useProfile, type Profile, type UserRole } from './SessionProvider';

/**
 * `Map<MenuKey, Set<UserRole>>` — keyed by menu_key, each value is the
 * set of roles for which `visible=true`. This is the shape every
 * consumer wants: "is role X allowed to see menu Y?" becomes a single
 * map lookup. Visitante is not stored here — `canSeeMenu` short-
 * circuits to true for visitante (mirrors `hasRole`).
 */
export type MenuVisibility = Map<MenuKey, Set<UserRole>>;

export const MENU_PERMISSIONS_QUERY_KEY = ['menu_permissions'] as const;

function buildFallbackVisibility(): MenuVisibility {
  const map = new Map<MenuKey, Set<UserRole>>();
  for (const [menuKey, roles] of Object.entries(MENU_PERMISSIONS_FALLBACK)) {
    map.set(menuKey as MenuKey, new Set(roles));
  }
  return map;
}

function buildVisibilityFromRows(rows: authApi.MenuPermissionRow[]): MenuVisibility {
  const map = new Map<MenuKey, Set<UserRole>>();
  for (const row of rows) {
    if (!row.visible) continue;
    if (!isMenuKey(row.menu_key)) continue;
    let set = map.get(row.menu_key);
    if (!set) {
      set = new Set<UserRole>();
      map.set(row.menu_key, set);
    }
    set.add(row.perfil);
  }
  return map;
}

/**
 * Fetches the nav visibility matrix and returns it as a `MenuVisibility`
 * map. Country-agnostic (same matrix for AR + MX), so no country code in
 * the queryKey. Window-focus refetch picks up admin edits within seconds
 * of a tab switch — no realtime subscription needed in Phase 1.
 *
 * On fetch failure or before resolution, returns
 * `MENU_PERMISSIONS_FALLBACK` so the sidebar always renders something
 * sensible. The fallback IS the Day-1 spec: it must stay in sync with
 * the role tuples in `router.tsx` / `AppShell.tsx` (post-Phase 2 those
 * tuples are replaced by `menuKey` lookups, removing the drift surface).
 */
export function useMenuPermissions(): {
  visibility: MenuVisibility;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: MENU_PERMISSIONS_QUERY_KEY,
    queryFn: authApi.fetchMenuPermissions,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: true,
  });

  const visibility = useMemo<MenuVisibility>(() => {
    if (!query.data) return buildFallbackVisibility();
    return buildVisibilityFromRows(query.data);
  }, [query.data]);

  return {
    visibility,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * Non-hook variant. Use in places that already have a profile + the
 * visibility map (e.g. inside a parent component that called the hook
 * once and wants to filter a list of menu_keys without per-item hook
 * calls). Honors the visitante universal-true bypass.
 */
export function canSeeMenu(
  profile: Profile | null,
  visibility: MenuVisibility,
  menuKey: MenuKey,
): boolean {
  if (!profile) return false;
  if (profile.perfil === 'visitante') return true;
  return visibility.get(menuKey)?.has(profile.perfil) ?? false;
}

/** Hook variant — convenient when a component only cares about one menu_key. */
export function useCanSeeMenu(menuKey: MenuKey): boolean {
  const profile = useProfile();
  const { visibility } = useMenuPermissions();
  return canSeeMenu(profile, visibility, menuKey);
}
